import { config } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { XStack } from '@/components/ui/layout/layout';
import { meetingStore } from '@/features/meetings/meetings.state';

import { meetingDebugStore } from '../meeting-debug.store';
import {
  describePresence,
  formatInactivityCountdown,
} from '../meeting-debug.utils';

import { MeetingDebugSection } from './meeting-debug-section';

const AGE_TICK_MS = 1000;

function KeyValue({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  return (
    <XStack className="justify-between items-baseline py-0.5">
      <span className="text-xs text-foreground-muted shrink-0">{label}</span>
      {copyable ? (
        <button
          type="button"
          className="text-xs text-foreground text-right truncate ml-3 font-mono tabular-nums select-text cursor-pointer hover:underline bg-transparent p-0 border-0"
          title="Click to copy"
          aria-label={`Copy ${label}`}
          onClick={() =>
            void navigator.clipboard
              .writeText(value)
              .then(() => toast.success(`${label} copied`))
          }
        >
          {value}
        </button>
      ) : (
        <span className="text-xs text-foreground text-right truncate ml-3 font-mono tabular-nums">
          {value}
        </span>
      )}
    </XStack>
  );
}

function formatAge(nowMs: number, since: Date | undefined): string {
  if (!since) return '-';
  return `${Math.max(0, Math.round((nowMs - since.getTime()) / 1000))}s`;
}

const useNowMs = (active: boolean) => {
  const [nowMs, setNowMs] = useState(Date.now);

  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => setNowMs(Date.now()), AGE_TICK_MS);
    return () => clearInterval(interval);
  }, [active]);

  return nowMs;
};

export const MeetingDebugSessionCard = observer(
  ({ meetingId }: { meetingId?: string }) => {
    const { recording } = meetingStore;
    const hasLiveSession = meetingId
      ? meetingStore.isMeetingInRecordingLifecycle(meetingId)
      : recording.status !== 'idle';
    const nowMs = useNowMs(hasLiveSession);

    if (!hasLiveSession || recording.status === 'idle') {
      return (
        <MeetingDebugSection title="Current session">
          <p className="text-xs text-foreground-muted">
            {meetingId ? 'No live session for this meeting' : 'No live session'}
          </p>
          {meetingId && (
            <KeyValue label="Meeting id" value={meetingId} copyable />
          )}
        </MeetingDebugSection>
      );
    }

    const latestDetection = meetingDebugStore.latestDetection(
      recording.meetingId
    );

    return (
      <MeetingDebugSection title="Current session">
        <KeyValue label="Recording state" value={recording.status} />
        <KeyValue label="Meeting id" value={recording.meetingId} copyable />
        <KeyValue
          label="Start reason"
          value={meetingStore.recordingSessionStartReason ?? '-'}
        />
        <KeyValue
          label="Bound app"
          value={meetingStore.recordingSessionAppBundleId ?? '-'}
        />
        <KeyValue
          label="Transcription engine"
          value={recording.activeTranscriptionEngine ?? '-'}
        />
        <KeyValue
          label="Started at"
          value={
            'startedAt' in recording
              ? recording.startedAt.toLocaleTimeString()
              : '-'
          }
        />
        <KeyValue
          label="Latest detection"
          value={latestDetection?.summary ?? '-'}
        />
        <KeyValue
          label="Presence"
          value={describePresence(
            meetingStore.recordingSessionAppBundleId,
            latestDetection
          )}
        />
        <KeyValue
          label="Inactivity auto-stop"
          value={formatInactivityCountdown({
            timeoutMinutes: config.meetings.auto_stop_timeout,
            lastActivityAt:
              meetingStore.lastTranscriptReceivedAt ??
              ('startedAt' in recording ? recording.startedAt : undefined),
            nowMs,
          })}
        />
        <KeyValue
          label="Transcriber state"
          value={meetingDebugStore.heartbeatHealth?.transcriberState ?? '-'}
        />
        <KeyValue
          label="Since last transcript"
          value={formatAge(nowMs, meetingStore.lastTranscriptReceivedAt)}
        />
        <KeyValue
          label="Since last audio"
          value={formatAge(nowMs, meetingStore.lastAudioReceivedAt)}
        />
      </MeetingDebugSection>
    );
  }
);
